import {
  Configure,
  ConfigureProps,
  InstantSearch,
  useCurrentRefinements,
  useInstantSearch,
} from 'react-instantsearch';
import TypesenseInstantsearchAdapter from 'typesense-instantsearch-adapter';
import { graphql, Link } from 'gatsby';
import React, { PropsWithChildren, useRef, useEffect } from 'react';
import { SEO } from '../../components';
import { Pagination, SearchBox } from 'react-instantsearch';
import type { Hit } from 'instantsearch.js';
import { removeHyphens } from '@src/utils/utils';
import { useIsQbSubscribed } from '@src/hooks/user';
import SearchFilters from '@src/components/UI/QuestionBank/SearchFilters';
import ViewedProgress from '@src/components/Search/ViewedProgress';
import QuestionBankGuard from '@src/guards';
import { WorkSheetMaker } from 'components/WorksheetMaker';
import { QuestionBankSchema } from '@src/constants/schemas';
import BlockFactory from '@src/components/BlockFactory';
import { worksheetActions, useAllSelected } from '@src/stores/worksheet.store';

// Styles
import 'yet-another-react-lightbox/styles.css';
import 'instantsearch.css/themes/satellite.css';
import * as styles from './question-bank.module.css';
import ReactPlayer from 'react-player';
import HitList from '@src/components/QuestionBank/HitList';
import { useOnMount } from '@src/hooks/useOnMount';
import { questionBankHitsActions } from '@src/stores/question-bank-hits.store';
import { createFacetRankingSearchClient } from '@src/utils/question-bank';

export const typesenseInstantsearchAdapter = new TypesenseInstantsearchAdapter({
  server: {
    apiKey: process.env.GATSBY_TYPESENSE_SEARCH_ONLY_API_KEY, // Be sure to use the search-only-api-key
    nodes: [
      {
        host: process.env.GATSBY_TYPESENSE_HOST,
        port: 443,
        protocol: 'https',
      },
    ],
  },
  collectionSpecificSearchParameters: {
    questions: {
      query_by:
        'slug,resource_type,qualification,series,area,paper,difficulty,subtopic,division,keyword,question_image_text',
      sort_by: 'series:asc,paper:asc,question_number:asc',
    },
  },
});

export const typesenseClient = typesenseInstantsearchAdapter.typesenseClient;

const searchClient = createFacetRankingSearchClient(typesenseInstantsearchAdapter.searchClient);

const future = { preserveSharedStateOnUnmount: true };

const PromotionVideo = ({ src }: { src: string }) => {
  return <ReactPlayer className={styles.promotionVideo} controls url={src} />;
};

const Subsribe = ({ children }: PropsWithChildren) => {
  const { results } = useInstantSearch();
  const filters = useCurrentRefinements();

  const hits = filters.items.length ? results.nbHits : 0;

  return (
    <div className={styles.subscription}>
      <div className={styles.subscriptionText}>
        <p>You need to be subscribed to view the results of this question bank.</p>
        <p>
          The search is available above so you can see all the search filters offered and whether we
          have documents for your course.
        </p>
        <p>
          Based on the current filters we would show <b>{hits}</b> questions along with their mark
          schemes and written mark schemes.
        </p>
        <Link className="button w-button m-0" to="/subscriptions">
          Subscribe
        </Link>
      </div>
      {children}
    </div>
  );
};

const SelectAll = () => {
  const { results } = useInstantSearch();
  const allSelected = useAllSelected();

  const handleSelectAll = () => {
    if (allSelected) return;

    results.hits.forEach((hit: Hit) => {
      worksheetActions.addQuestion({
        title: `${removeHyphens(hit.series)} - ${removeHyphens(hit.paper)} -  Q${
          hit.question_number
        }`,
        slug: hit.slug,
      });
    });
    worksheetActions.setAllSelected(true);
  };

  const handleClearPage = () => {
    results.hits.forEach((hit: Hit) => {
      worksheetActions.removeQuestion(hit.slug);
    });
    worksheetActions.setAllSelected(false);
  };

  // Reset allSelected when hits change (page change or search)
  useEffect(() => {
    worksheetActions.setAllSelected(false);
  }, [results.hits]);

  useOnMount(() => {
    questionBankHitsActions.reset();
  });

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
      <button
        className="button w-button"
        onClick={handleSelectAll}
        style={{ opacity: allSelected ? 0.7 : 1, marginLeft: 0 }}
        disabled={allSelected}
      >
        Select All
      </button>
      <button className="button w-button" onClick={handleClearPage}>
        Clear
      </button>
    </div>
  );
};

const QuestionBank = ({ data: { datoCmsPage } }) => {
  const { customContent } = datoCmsPage;

  const containerRef = useRef<HTMLDivElement>(null);

  const goToTop = () => {
    containerRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const configureProps = {
    hitsPerPage: 12,
  } as ConfigureProps;

  const isSubscribed = useIsQbSubscribed();

  return (
    <>
      <SEO
        noIndex={false}
        title={datoCmsPage?.seo?.title}
        seoKeywords={datoCmsPage?.seoKeywords}
        description={datoCmsPage?.seo?.description}
        image={datoCmsPage?.seo?.image?.url}
        schema={QuestionBankSchema}
      />

      <WorkSheetMaker />

      <InstantSearch searchClient={searchClient} indexName="questions" future={future} routing>
        <Configure {...configureProps} />
        <article>
          <div className="container">
            <div>
              <div className="container" ref={containerRef}>
                <div className={styles.searchPanel}>
                  <SearchFilters />
                  <div className={styles.searchPanelResults}>
                    <SearchBox placeholder="" className={styles.searchbox} />

                    {isSubscribed && <SelectAll />}

                    {isSubscribed ? (
                      <HitList />
                    ) : (
                      <Subsribe>
                        {datoCmsPage?.video && <PromotionVideo src={datoCmsPage?.youtubeVideo} />}
                      </Subsribe>
                    )}

                    {isSubscribed && (
                      <div className={styles.pagination}>
                        <QuestionBankGuard />
                        <ViewedProgress prefix="You've viewed" suffix="Questions" />
                        <Pagination onClick={goToTop} />
                        <button className="ais-Pagination-link" onClick={goToTop}>
                          Return to top
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </InstantSearch>
      {!isSubscribed && (
        <div className="container" style={{ marginTop: '2rem' }}>
          <BlockFactory blocks={customContent} />
        </div>
      )}
    </>
  );
};

export default QuestionBank;

const query = graphql`
  query {
    datoCmsPage(slug: { eq: "question-bank" }) {
      video {
        url
      }
      seoKeywords {
        id
        keyword
      }
      seo {
        description
        image {
          url
        }
        title
      }
      youtubeVideo
      ...CustomContentFragment
    }
  }
`;
export { query };
